<template>
  <div>
    <div class="row mb-3">
      <h3 class="col-sm-9 heading semi-bold pull-left">
        Shirt Gallery Image Processing Job Overview
      </h3>
      <div class="col-md-3 pull-right text-right">
        <button
          class="btn btn-primary"
          @click="startProcessing({ skip_cleaning_temp: false })"
          :class="{ disabled: disableActions }"
          v-if="!isReadOnlyUser"
        >
          Begin Processing
        </button>
      </div>
    </div>

    <ul class="nav nav-tabs" role="tablist">
      <li class="active">
        <a
          href="#completed-jobs"
          @click="reloadTable('completed')"
          role="tab"
          data-toggle="tab"
          aria-expanded="true"
        >
          Completed Jobs
        </a>
      </li>
      <li class="">
        <a
          href="#failed-jobs"
          @click="reloadTable('failed')"
          role="tab"
          data-toggle="tab"
          aria-expanded="false"
        >
          Failed Jobs
        </a>
      </li>
    </ul>

    <div class="tab-content">
      <div class="tab-pane active" id="completed-jobs">
        <div class="grid bg-white">
          <vue-good-table
            mode="remote"
            :totalRows="totalRecords"
            :isLoading.sync="isLoading"
            :pagination-options="{
              enabled: true,
              mode: 'pages',
            }"
            styleClass="table table-bordered"
            :rows="rows"
            :columns="completedJobColumns"
          >
            <template slot="table-row" slot-scope="props">
              <span v-if="props.column.field == 'action_id'">
                <button
                  class="btn btn-primary"
                  v-if="
                    props.row.images_to_process !== props.row.images_processed
                  "
                  @click="
                    startProcessing({ skip_cleaning_temp: true }, 'completed')
                  "
                  :class="{ disabled: disableActions }"
                >
                  Resume
                </button>
              </span>
              <span v-else>
                {{ props.formattedRow[props.column.field] }}
              </span>
            </template>
          </vue-good-table>
        </div>
      </div>

      <div class="tab-pane" id="failed-jobs">
        <div class="grid bg-white">
          <vue-good-table
            mode="remote"
            :totalRows="totalRecords"
            :isLoading.sync="isLoading"
            :pagination-options="{
              enabled: true,
              mode: 'pages',
            }"
            styleClass="table table-bordered"
            :rows="rows"
            :columns="failedJobColumns"
          >
            <template slot="table-row" slot-scope="props">
              <span v-if="props.column.field == 'action_id'">
                <button
                  class="btn btn-primary"
                  v-if="!isReadOnlyUser"
                  @click="
                    startProcessing({
                      job_id: props.row.id,
                      skip_cleaning_temp: false,
                    })
                  "
                  :class="{ disabled: disableActions }"
                >
                  Retry (images re-uploaded)
                </button>
                <button
                  class="btn btn-primary"
                  v-if="!isReadOnlyUser"
                  @click="
                    startProcessing({
                      job_id: props.row.id,
                      skip_cleaning_temp: true,
                    })
                  "
                  :class="{ disabled: disableActions }"
                >
                  Retry (without re-upload)
                </button>
                <button
                  class="btn btn-danger"
                  v-if="!isReadOnlyUser"
                  @click="deleteFailedJob(props.row.id)"
                  :class="{ disabled: disableActions }"
                >
                  Delete
                </button>
              </span>
              <span v-else-if="props.column.field == 'exception'">
                <button
                  class="btn btn-secondary"
                  @click="showExceptionInModal(props.row.id)"
                >
                  View Error Details
                </button>
              </span>
              <span v-else>
                {{ props.formattedRow[props.column.field] }}
              </span>
            </template>
          </vue-good-table>
        </div>
      </div>
    </div>

    <!-- Modal -->
    <div
      class="modal fade"
      id="modal-box"
      tabindex="-1"
      role="dialog"
      aria-labelledby="modelTitleId"
      aria-hidden="true"
    >
      <div class="modal-dialog" role="document">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title text-bold">Error Details</h5>
          </div>
          <div class="modal-body bg-white">
            <div id="exception-container">
              {{ exceptionDetails }}
            </div>
          </div>
          <div class="modal-footer">
            <button
              type="button"
              class="btn btn-secondary"
              data-dismiss="modal"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
    <!-- modal -->
  </div>
</template>

<script>
import { TableHelper } from "@/pages/Tools/TableHelper.js";
import { VueGoodTable } from "vue-good-table";

export default {
  data() {
    return {
      helper: null,
      isLoading: false,
      isReadOnlyUser: false,
      disableActions: false,
      rows: [],
      totalRecords: 0,
      jobType: "completed",
      completedJobColumns: [
        {
          label: "ID",
          field: "id",
          tdClass: "text-center",
          thClass: "text-center",
        },
        {
          label: "Date",
          field: "created_at",
          tdClass: "text-center",
          thClass: "text-center",
        },
        {
          label: "Directory Images",
          field: "images_to_process",
          tdClass: "text-center",
          thClass: "text-center",
        },
        {
          label: "Resized",
          field: "images_resized",
          tdClass: "text-center",
          thClass: "text-center",
        },
        {
          label: "Optimized",
          field: "images_optimized",
          tdClass: "text-center",
          thClass: "text-center",
        },
        {
          label: "Uploaded",
          field: "images_uploaded",
          tdClass: "text-center",
          thClass: "text-center",
        },
        {
          label: "Attached",
          field: "images_attached",
          tdClass: "text-center",
          thClass: "text-center",
        },
        {
          label: "Processed",
          field: "images_processed",
          tdClass: "text-center",
          thClass: "text-center",
        },
        {
          label: "Action",
          field: "action_id",
          sortable: false,
          tdClass: "text-center",
          thClass: "text-center",
          width: "15%",
        },
      ],
      failedJobColumns: [
        {
          label: "ID",
          field: "id",
          tdClass: "text-center col-sm-1",
          thClass: "text-center",
        },
        {
          label: "Date",
          field: "failed_at",
          tdClass: "text-center col-sm-1",
          thClass: "text-center",
        },
        {
          label: "Details",
          field: "exception",
          tdClass: "text-center col-sm-2",
          thClass: "text-center",
        },
        {
          label: "Action",
          field: "action_id",
          sortable: false,
          tdClass: "text-right col-sm-6",
          thClass: "text-center",
          width: "15%",
        },
      ],
      exceptionDetails: "",
    };
  },
  mounted() {
    this.isReadOnlyUser = this.$auth.user().role === "read_only";
    this.helper = new TableHelper({
      parent: this,
      endpoint: "settings/image_gallery_processor",
      sort: {
        created_at: "desc",
      },
    });
  },
  components: {
    VueGoodTable,
  },
  methods: {
    async reloadTable(type) {
      try {
        this.jobType = type;
        this.helper.replaceSort({
          sort:
            type === "completed"
              ? {
                  created_at: "desc",
                }
              : {
                  failed_at: "desc",
                },
        });
        this.helper.reloadData();
      } catch (e) {
        console.log(e);
        toastr.error("Unable to load " + type + " jobs");
      }
    },

    getCustomListingParams() {
      return {
        job_type: this.jobType,
      };
    },

    async startProcessing(options, type = "completed") {
      try {
        this.disableActions = true;
        let res = await axios.post("settings/image_gallery_processor", options);
        if (res) {
          if (type == "completed") {
            // switch to this tab!
            $('.nav-tabs a[href="#completed-jobs"]').tab("show");
          }
          this.reloadTable(type);
          toastr.success("The job has been queued successfully");
          this.disableActions = false;
        }
      } catch (error) {
        if (error.response.status === 403) {
          toastr.error(
            "Not authorized, please ensure that you have the required permissions."
          );
        } else {
          toastr.error("There was some errors, please correct and try again");
        }
        this.disableActions = false;
      }
    },

    async deleteFailedJob(jobId) {
      try {
        this.disableActions = true;
        let res = await axios.delete(
          "settings/image_gallery_processor/" + jobId
        );
        if (res) {
          this.reloadTable("failed");
          toastr.success("Success");
          this.disableActions = false;
        }
      } catch (error) {
        if (error.response.status === 403) {
          toastr.error(
            "Not authorized, please ensure that you have the required permissions."
          );
        } else {
          toastr.error("There was some errors, please correct and try again");
        }
        this.disableActions = false;
      }
    },

    showExceptionInModal(id) {
      let failedJob = this.rows.filter((job) => job.id === id);
      if (failedJob.length === 1) {
        this.exceptionDetails = failedJob[0].exception;
        show_modal("#modal-box");
      } else {
        toastr.error("Unable to find Job!");
      }
    },
  },
};
</script>

<style>
#exception-container {
  height: 500px;
  overflow-y: scroll;
}
</style>
